import React from 'react';
import { I18n } from '@fiverr-private/i18n-react';
import { Heading } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { ComplianceIcon } from '@fiverr-private/visuals';
import { SellerDocumentsList } from '@fiverr-private/legal_documents_package';
import { useComplianceContext } from '../ComplianceProvider';
import { FirstTimeComplianceSection } from '../FirstTimeComplianceSection';
import { ComplianceTypes } from '../ComplianceModal/types';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { reportEventsApi } from '../../biEvents';
import { usePayment } from '../../hooks/usePayment';
import { BackgroundCheckCollapsibleSection } from './complianceSectionComponents/BackgroundCheckCollapsibleSection';
import { ComplianceCallToAction } from './complianceSectionComponents/ComplianceCallToAction';
import { WorkforceClassificationCollapsibleSection } from './complianceSectionComponents/WorkforceClassificationCollapsibleSection';

export const ComplianceSection = ({
    requeseteeId,
    requeseteeUserName,
    isWithEntryPoint = true,
    viewType = '',
}: {
    requeseteeId: number;
    requeseteeUserName: string;
    isWithEntryPoint?: boolean;
    viewType?: string;
}) => {
    const context = useComplianceContext();
    const {
        legalDocsConfigurations,
        legalDocumentsPackageData,
        shouldShowWorkforceClassification,
        sellerEntityType,
        isEligibleForBackgroundCheck,
        shouldShowLegalDocs,
        shouldShowBackgroundCheck,
    } = useSellerConfigData(requeseteeId);

    const { handlePayment } = usePayment();

    const openModalOptions = { sellerId: requeseteeId, sellerName: requeseteeUserName };

    if (!context) return null;

    const sellerComplianceState = context.getSellerComplianceState(requeseteeId);

    if (!sellerComplianceState.hasComplianceProcess && !sellerComplianceState.hasLegalDocsSetup && isWithEntryPoint) {
        return (
            <FirstTimeComplianceSection
                viewType={viewType}
                requeseteeId={requeseteeId}
                requeseteeUserName={requeseteeUserName}
            />
        );
    }

    const hideSectionDetails = viewType === 'inbox';
    const isWithComplianceIcon = sellerEntityType !== 'agency';

    return (
        <Stack direction={'column'} width={'100%'}>
            <Heading size={isWithComplianceIcon ? 'h_xxs' : 'h_md'}>
                <Stack gap="1.5">
                    {isWithComplianceIcon && (
                        <Container paddingY="0.5">
                            <ComplianceIcon />
                        </Container>
                    )}
                    <I18n k={`compliance.compliance_section.title`} />
                </Stack>
            </Heading>
            <Stack direction={'column'} gap={'4'}>
                {shouldShowLegalDocs && (
                    <SellerDocumentsList
                        biData={{ pageName: context.pageName ?? '' }}
                        sellerId={requeseteeId}
                        sellerName={requeseteeUserName}
                        initialData={sellerComplianceState?.legalDocsInitialData}
                        configurations={legalDocsConfigurations}
                        activeTemplates={context?.complianceData?.legalDocsData?.activeTemplates}
                        totalTemplates={context?.complianceData?.legalDocsData?.totalTemplates}
                        hideTitleDescription={hideSectionDetails}
                        handlePayment={(documentIds: string[]) =>
                            handlePayment({
                                templateIds: documentIds,
                                complianceType: ComplianceTypes.LEGAL_DOCS,
                                requeseteeUserName,
                                sellerId: requeseteeId,
                                isFree: legalDocumentsPackageData?.isFree,
                            })
                        }
                        sellerEntityType={sellerEntityType}
                    />
                )}
                {isEligibleForBackgroundCheck &&
                    shouldShowBackgroundCheck &&
                    (sellerComplianceState.isSellerHasBackgroundCheckProcess ? (
                        <BackgroundCheckCollapsibleSection
                            requeseteeId={requeseteeId}
                            requeseteeUserName={requeseteeUserName}
                        />
                    ) : (
                        <ComplianceCallToAction
                            complianceType={ComplianceTypes.BACKGROUND_CHECK}
                            onClick={() => {
                                reportEventsApi.clickedOnBackgroundCheck({
                                    pageName: context.pageName,
                                    sellerId: requeseteeId,
                                    type: 'section',
                                });

                                context?.openBackgroundCheckModal(openModalOptions);
                            }}
                            hideDetails={hideSectionDetails}
                        />
                    ))}
                {shouldShowWorkforceClassification &&
                    (sellerComplianceState.isSellerHasClassificationProcess ? (
                        <WorkforceClassificationCollapsibleSection
                            requeseteeId={requeseteeId}
                            requeseteeUserName={requeseteeUserName}
                        />
                    ) : (
                        <ComplianceCallToAction
                            complianceType={ComplianceTypes.WORKFORCE_CLASSIFICATION}
                            onClick={() => {
                                reportEventsApi.clickedOnRequestAudit({
                                    pageName: context.pageName,
                                    sellerId: requeseteeId,
                                    type: 'section',
                                });

                                context?.openWFCModal(openModalOptions);
                            }}
                            hideDetails={hideSectionDetails}
                        />
                    ))}
            </Stack>
        </Stack>
    );
};
